import { memo, Suspense, lazy, useCallback, useState } from "react";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { Home, Search, User as UserIcon } from "lucide-react";
import { MayaMark } from "./BrandLockup";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { useNavAutohide } from "@/hooks/use-nav-autohide";



// The search dialog (and the catalog code it pulls in) is only needed once the
// user actually taps "Buscar", so it stays out of the first-paint bundle.
const SearchDialog = lazy(() =>
  import("./SearchDialog").then((m) => ({ default: m.SearchDialog })),
);

/**
 * Mobile-only bottom navigation. Sits fixed at the bottom of the viewport
 * on all pages, with the center "Maya" slot elevated to open the Maya
 * universe (chat + recommendations + premium). Hides itself while the user
 * is inside an orbit universe (?orbit=…) so it doesn't compete with the
 * "Regresar" button.
 */
function BottomNavImpl() {
  const { t } = useTranslation();
  const router = useRouter();
  const autohide = useNavAutohide();
  const { user } = useAuth();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMounted, setSearchMounted] = useState(false);


  // Select only the primitives we need so the bar does not re-render on every
  // unrelated location change (search params, hash, state, etc.).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hasOrbit = useRouterState({
    select: (s) => Boolean((s.location.search as Record<string, unknown> | undefined)?.orbit),
  });

  const openSearch = useCallback(() => {
    setSearchMounted(true);
    setSearchOpen(true);
  }, []);

  const openMaya = useCallback(() => {
    // The central Maya button opens the full ConciergeWidget panel
    // (chat, smart suggestions, trip planning, tour comparator, human
    // advisor, "Mis compras", etc.) — same experience the floating
    // widget used to provide on desktop.
    window.dispatchEvent(new CustomEvent("concierge:open"));
  }, []);

  // Warm the route (chunk + loader) as soon as the finger touches down, so the
  // actual navigation on tap-release has nothing left to fetch.
  const warm = useCallback(
    (to: string) => {
      void router.preloadRoute({ to }).catch(() => {});
    },
    [router],
  );

  // Hide while a Maya universe is active so the fixed "Regresar" button
  // stays reachable and the universe UI is not crowded.
  if (hasOrbit) return null;

  const isActive = (href: string, exact = false) =>
    exact ? pathname === href : pathname === href || pathname.startsWith(`${href}/`);

  return (
    <>
    <nav
      data-bottom-nav
      aria-label={t("nav.bottom.aria", "Navegación principal")}
      className={`lg:hidden pointer-events-none fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ease-out will-change-transform ${
        autohide ? "translate-y-full" : "translate-y-0"
      }`}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >

      <div
        className="pointer-events-auto relative mx-auto grid h-16 w-full grid-cols-4 items-end border-t border-amber-300/20 bg-black/80 px-2 backdrop-blur-xl shadow-[0_-8px_24px_rgba(0,0,0,0.35)]"
        style={{ touchAction: "manipulation" }}
      >
        <NavItem
          to="/"
          label={t("nav.bottom.home", "Inicio")}
          active={isActive("/", true)}
          onWarm={warm}
          icon={<Home className="h-5 w-5" />}
        />
        <button
          type="button"
          onPointerDown={openSearch}
          onClick={openSearch}
          aria-label={t("nav.bottom.search", "Buscar")}
          className="flex h-full w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-[10px] font-medium text-white/70 transition-transform duration-100 hover:text-white active:scale-95 active:text-amber-300 focus:outline-none [touch-action:manipulation]"
        >
          <Search className="h-5 w-5" />
          <span className="leading-none">{t("nav.bottom.search", "Buscar")}</span>
        </button>

        {/* Central slot — reserved space so nothing overlaps the FAB */}
        <div aria-hidden className="h-full w-full" />

        <NavItem
          to={user ? "/mi-cuenta" : "/auth"}
          label={t("nav.bottom.profile", "Perfil")}
          active={isActive("/mi-cuenta") || isActive("/auth")}
          onWarm={warm}
          icon={<UserIcon className="h-5 w-5" />}
        />

        {/* Central elevated Maya button — pinned to the exact horizontal center */}
        <button
          type="button"
          onPointerDown={openMaya}
          aria-label={t("nav.bottom.maya", "Maya")}
          className="group absolute bottom-2.5 left-[62.5%] flex -translate-x-1/2 flex-col items-center focus:outline-none [touch-action:manipulation]"
        >
          <MayaMark size={56} className="shadow-[0_10px_28px_-6px_rgba(212,160,23,0.7)] transition-transform duration-100 group-active:scale-95" />
          <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
            {t("nav.bottom.maya", "Maya")}
          </span>
        </button>
      </div>

    </nav>

    {searchMounted && (
      <Suspense fallback={null}>
        <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      </Suspense>
    )}
    </>
  );
}

export const BottomNav = memo(BottomNavImpl);

const NavItem = memo(function NavItem({
  to,
  label,
  icon,
  active,
  onWarm,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onWarm: (to: string) => void;
}) {
  return (
    <Link
      to={to}
      preload="intent"
      onPointerDown={() => onWarm(to)}
      className={`flex h-full flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-[10px] font-medium transition-transform duration-100 active:scale-95 [touch-action:manipulation] ${
        active ? "text-amber-300" : "text-white/70 hover:text-white active:text-amber-300"
      }`}
    >
      {icon}
      <span className="leading-none">{label}</span>
    </Link>
  );
});
